export class GestureManager {
    constructor(element, callbacks) {
        this.element = element;
        this.callbacks = callbacks;
        this.multiTouchActive = false;
        
        // Configure element for proper touch handling
        this.element.style.touchAction = 'none';
        this.element.style.webkitTouchCallout = 'none';
        this.element.style.webkitUserSelect = 'none';
        this.element.style.userSelect = 'none';
        
        // Configure Hammer.js
        this.hammer = new Hammer(element);
        this.hammer.get('pan').set({ direction: Hammer.DIRECTION_ALL });
        this.hammer.get('swipe').set({ direction: Hammer.DIRECTION_ALL });
        // Explicitly disable doubletap to prevent unintended re-entrant actions
        if (this.hammer.get('doubletap')) {
            this.hammer.get('doubletap').set({ enable: false });
        }
        
        // Enable and configure pinch recognizer
        this.hammer.get('pinch').set({ 
            enable: true,
            threshold: 0.1
        });
        
        // Critical fix: Prevent pan during pinch completely
        const pinch = this.hammer.get('pinch');
        const pan = this.hammer.get('pan');
        
        // Make pan require pinch to fail - NO simultaneous recognition
        pan.requireFailure(pinch);
        
        // Do NOT use recognizeWith - this breaks gesture isolation
        
        // Prevent browsers from natively handling these events
        this.disableNativeTouchBehaviors();
        
        // Add event listeners
        this.hammer.on('panstart', this.handlePanStart.bind(this));
        this.hammer.on('panmove', this.handlePanMove.bind(this));
        this.hammer.on('panend', this.handlePanEnd.bind(this));
        this.hammer.on('swipe', this.handleSwipe.bind(this));
        // Do not bind doubletap to avoid duplicate triggers in zoomed view
        this.hammer.on('tap', this.handleTap.bind(this));
        
        // Add pinch event listeners
        this.hammer.on('pinchstart', this.handlePinchStart.bind(this));
        this.hammer.on('pinchin', this.handlePinchIn.bind(this));
        this.hammer.on('pinchout', this.handlePinchOut.bind(this));
        this.hammer.on('pinchend', this.handlePinchEnd.bind(this));
    }
    
    disableNativeTouchBehaviors() {
        // Gesture state management for proper isolation
        this.gestureState = {
            touchCount: 0,
            isPanBlocked: false,
            isPinchActive: false
        };
        
        // Immediate gesture state tracking - blocks pan before Hammer.js recognition
        const trackGestureState = (e) => {
            const touchCount = e.touches ? e.touches.length : 0;
            const wasMultiTouch = this.multiTouchActive;
            
            // Update gesture state immediately
            this.gestureState.touchCount = touchCount;
            this.multiTouchActive = touchCount > 1;
            
            // Block pan immediately on multi-touch - don't wait for Hammer.js
            if (touchCount >= 2) {
                this.gestureState.isPanBlocked = true;
                this.gestureState.isPinchActive = true;
            } else if (touchCount === 0) {
                // Reset gesture state when all touches end
                this.gestureState.isPanBlocked = false;
                this.gestureState.isPinchActive = false;
            }
            
            // Notify ContactSheet of multi-touch state changes
            if (this.callbacks.onMultiTouchChange && wasMultiTouch !== this.multiTouchActive) {
                this.callbacks.onMultiTouchChange(this.multiTouchActive);
            }
            
            e.preventDefault();
        };
        
        const preventDefaultTouch = (e) => e.preventDefault();
        
        this.element.addEventListener('touchstart', trackGestureState, { passive: false });
        this.element.addEventListener('touchmove', trackGestureState, { passive: false });
        this.element.addEventListener('touchend', trackGestureState, { passive: false });
        this.element.addEventListener('gesturestart', preventDefaultTouch, { passive: false });
        this.element.addEventListener('gesturechange', preventDefaultTouch, { passive: false });
        this.element.addEventListener('gestureend', preventDefaultTouch, { passive: false });
        
        // Store event handlers for cleanup
        this.touchHandlers = [
            { event: 'touchstart', handler: trackGestureState },
            { event: 'touchmove', handler: trackGestureState },
            { event: 'touchend', handler: trackGestureState },
            { event: 'gesturestart', handler: preventDefaultTouch },
            { event: 'gesturechange', handler: preventDefaultTouch },
            { event: 'gestureend', handler: preventDefaultTouch }
        ];
    }
    
    handlePanStart(event) {
        // Deterministic gesture isolation - block pan if pinch is possible
        if (this.gestureState.isPanBlocked || this.gestureState.isPinchActive) {
            return;
        }
        
        if (this.callbacks.onPanStart) {
            this.callbacks.onPanStart(event);
        }
    }
    
    handlePanMove(event) {
        // Deterministic gesture isolation - block pan if pinch is active
        if (this.gestureState.isPanBlocked || this.gestureState.isPinchActive) {
            return;
        }
        
        if (this.callbacks.onPanMove) {
            this.callbacks.onPanMove(event);
        }
    }
    
    handlePanEnd(event) {
        // Deterministic gesture isolation - block pan if pinch was active
        if (this.gestureState.isPanBlocked || this.gestureState.isPinchActive) {
            return;
        }
        
        if (this.callbacks.onPanEnd) {
            this.callbacks.onPanEnd(event);
        }
    }
    
    handleSwipe(event) {
        if (this.callbacks.onSwipe) {
            this.callbacks.onSwipe(event);
        }
    }
    
    handleDoubleTap(event) {
        if (this.callbacks.onDoubleTap) {
            this.callbacks.onDoubleTap(event);
        }
    }
    
    handleTap(event) {
        // Ignore tap when Control key is held (macOS right-click)
        if (event.srcEvent && event.srcEvent.ctrlKey) {
            return;
        }
        
        if (this.callbacks.onTap) {
            this.callbacks.onTap(event);
        }
    }
    
    handlePinchStart(event) {
        if (this.callbacks.onPinchStart) {
            this.callbacks.onPinchStart(event);
        }
    }
    
    handlePinchIn(event) {
        if (this.callbacks.onPinchIn) {
            this.callbacks.onPinchIn(event);
        }
    }
    
    handlePinchOut(event) {
        if (this.callbacks.onPinchOut) {
            this.callbacks.onPinchOut(event);
        }
    }
    
    handlePinchEnd(event) {
        if (this.callbacks.onPinchEnd) {
            this.callbacks.onPinchEnd(event);
        }
    }
    
    dispose() {
        this.hammer.destroy();
        
        // Remove touch event listeners
        if (this.touchHandlers) {
            this.touchHandlers.forEach(({ event, handler }) => {
                this.element.removeEventListener(event, handler);
            });
        }
    }
} 